import React from 'react';
import { DocumentValidationResult } from '../../types/validation';
import Popover from '../ui/Popover';

interface ValidationIndicatorProps {
  validation?: DocumentValidationResult | null;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  showDetails?: boolean;
}

/**
 * Validation Indicator Component
 *
 * Shows a visual indicator of document validation status
 * with red flag for problems, green checkmark for valid docs.
 */
export const ValidationIndicator: React.FC<ValidationIndicatorProps> = ({
  validation,
  size = 'md',
  onClick,
  showDetails = false,
}) => {
  if (!validation) {
    return null;
  }

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };

  const issues = validation.issues || [];
  const criticalCount = issues.filter(i => i.severity === 'CRITICAL').length;
  const warningCount = issues.filter(i => i.severity === 'WARNING').length;

  const getIndicatorColor = () => {
    if (!validation.isValid || criticalCount > 0) {
      return 'bg-red-500 text-white';
    } else if (warningCount > 0) {
      return 'bg-amber-500 text-white';
    } else {
      return 'bg-green-500 text-white';
    }
  };

  const getIndicatorIcon = () => {
    if (!validation.isValid || criticalCount > 0) {
      return '🚩'; // Red flag
    } else if (warningCount > 0) {
      return '⚠️'; // Warning
    } else {
      return '✓'; // Checkmark
    }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button
        onClick={onClick}
        className={`
          ${sizeClasses[size]}
          ${getIndicatorColor()}
          rounded-full
          flex items-center justify-center
          font-bold
          shadow-md
          hover:shadow-lg
          transition-all
          ${onClick ? 'cursor-pointer hover:scale-110' : 'cursor-default'}
        `}
        title={
          validation.isValid
            ? `Valid (Score: ${validation.overallScore}/100)`
            : `Invalid (${criticalCount} critical issues)`
        }
      >
        {getIndicatorIcon()}
      </button>

      {showDetails && (
        <div className="flex flex-col text-xs">
          <div className="font-semibold text-gray-900">
            {validation.isValid ? 'Valid' : 'Invalid'}
          </div>
          <div className="text-gray-500">
            Score: {validation.overallScore}/100
          </div>
          {(criticalCount > 0 || warningCount > 0) && (
            <div className="text-gray-600">
              {criticalCount > 0 && (
                <span className="text-red-600">{criticalCount} critical </span>
              )}
              {warningCount > 0 && (
                <span className="text-amber-600">{warningCount} warnings</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface ValidationBadgeProps {
  validation?: DocumentValidationResult | null;
  onClick?: () => void;
  variant?: 'default' | 'compact' | 'icon';
}

/**
 * Validation Badge Component
 *
 * Shows validation status as a badge
 */
export const ValidationBadge: React.FC<ValidationBadgeProps> = ({
  validation,
  onClick,
  variant = 'default',
}) => {
  if (!validation) {
    return null;
  }

  const issues = validation.issues || [];
  const criticalCount = issues.filter(i => i.severity === 'CRITICAL').length;
  const warningCount = issues.filter(i => i.severity === 'WARNING').length;

  const state: 'error' | 'warn' | 'ok' =
    !validation.isValid || criticalCount > 0
      ? 'error'
      : warningCount > 0
        ? 'warn'
        : 'ok';

  const palette: Record<
    typeof state,
    { wrapper: string; dot: string; label: string; icon: string; aria: string }
  > = {
    error: {
      wrapper: 'bg-red-50 text-red-700 border-red-200',
      dot: 'bg-red-500',
      label:
        criticalCount === 1
          ? 'Invalid (1 issue)'
          : `Invalid (${criticalCount} issues)`,
      icon: '⚠️',
      aria: 'Document has critical validation issues',
    },
    warn: {
      wrapper: 'bg-amber-50 text-amber-700 border-amber-200',
      dot: 'bg-amber-400',
      label:
        warningCount === 1
          ? 'Valid (1 warning)'
          : `Valid (${warningCount} warnings)`,
      icon: '⚠️',
      aria: 'Document valid with warnings',
    },
    ok: {
      wrapper: 'bg-green-50 text-green-700 border-green-200',
      dot: 'bg-green-500',
      label: 'Validated',
      icon: '✓',
      aria: 'Document fully validated',
    },
  } as const;

  const selected = palette[state];

  if (variant === 'icon') {
    const count =
      state === 'error' ? criticalCount : state === 'warn' ? warningCount : 0;

    const validationDetails = (
      <div className="p-3 w-72 text-xs">
        <div className="font-semibold text-gray-900 mb-2">
          Document Validation
        </div>
        <div className="space-y-1.5">
          <div>
            <span className="font-medium">Status: </span>
            <span
              className={validation.isValid ? 'text-green-600' : 'text-red-600'}
            >
              {validation.isValid ? 'Valid' : 'Invalid'}
            </span>
          </div>
          <div>
            <span className="font-medium">Score: </span>
            {validation.overallScore}/100
          </div>
          {issues.length > 0 && (
            <div>
              <div className="font-medium mb-1">Issues:</div>
              <ul className="list-disc list-inside space-y-0.5 text-gray-600">
                {issues.map((issue, idx) => (
                  <li key={idx}>{issue.message}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );

    return (
      <Popover
        content={validationDetails}
        side="right"
        align="start"
        trigger="hover"
      >
        <button
          onClick={onClick}
          aria-label={selected.aria}
          className={`relative inline-flex items-center justify-center rounded-full border ${selected.wrapper} ${onClick ? 'cursor-pointer hover:shadow-sm transition' : 'cursor-help'}
            h-6 w-6 text-[11px] font-semibold leading-none`}
        >
          <span className="sr-only">{selected.label}</span>
          <span>{selected.icon}</span>
          {count > 0 && (
            <span
              className={`absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full text-[9px] flex items-center justify-center text-white ${state === 'error' ? 'bg-red-500' : state === 'warn' ? 'bg-amber-500' : 'bg-green-500'}`}
            >
              {count}
            </span>
          )}
        </button>
      </Popover>
    );
  }

  if (variant === 'compact') {
    const count =
      state === 'error' ? criticalCount : state === 'warn' ? warningCount : 0;
    return (
      <button
        onClick={onClick}
        aria-label={selected.aria}
        title={selected.label}
        className={`inline-flex items-center justify-center rounded-full border ${selected.wrapper} ${onClick ? 'cursor-pointer hover:shadow-sm transition' : 'cursor-default'}
          h-6 min-w-[1.75rem] px-1.5 text-[10px] font-semibold leading-none gap-1`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${selected.dot}`} />
        <span className="flex items-center gap-0.5">
          <span className="text-[11px]">{selected.icon}</span>
          {count > 0 && <span>{count}</span>}
        </span>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      aria-label={selected.aria}
      className={`group inline-flex items-center gap-1.5 pl-2 pr-2 py-1 text-[11px] font-medium rounded-full border leading-none align-middle
        ${selected.wrapper}
        ${onClick ? 'cursor-pointer hover:shadow-sm hover:bg-opacity-90 transition' : 'cursor-default'}
      `}
    >
      <span className={`w-2 h-2 rounded-full ${selected.dot}`} />
      <span className="tracking-tight flex items-center gap-1">
        <span>{selected.icon}</span>
        <span>{selected.label}</span>
      </span>
    </button>
  );
};
